import {
  AdapterIOChainKind,
  AdapterIODirection,
  AdapterIOStatus,
  AdapterIOStep,
  type AdapterIoEntityType,
  AdapterReviewSeverity,
  AdapterType
} from '@navio/nsdk';
import { constObjectToOptions } from '../options/choiceOptions';

const ADAPTER_IO_STATUS_LABEL_MAP: Record<AdapterIOStatus, string> = {
  [AdapterIOStatus.PENDING]: 'Pending',
  [AdapterIOStatus.SUCCESS]: 'Success',
  [AdapterIOStatus.FAILED]: 'Failed',
  [AdapterIOStatus.SKIPPED]: 'Skipped',
  [AdapterIOStatus.RETRYING]: 'Retrying'
};
export const ADAPTER_IO_STATUS_OPTIONS = constObjectToOptions(AdapterIOStatus, ADAPTER_IO_STATUS_LABEL_MAP);
export const ADAPTER_IO_STATUS_LABEL_BY_VALUE = ADAPTER_IO_STATUS_LABEL_MAP;

/** Narrow an API string (e.g. step status) to a step status enum. */
export const isAdapterIOStatus = (status: string): status is AdapterIOStatus => status in ADAPTER_IO_STATUS_LABEL_MAP;

const ADAPTER_IO_STEP_LABEL_MAP: Record<AdapterIOStep, string> = {
  [AdapterIOStep.PRE_CHECK]: 'Pre-check',
  [AdapterIOStep.FETCH]: 'Fetch',
  [AdapterIOStep.CUSTOMER]: 'Customer',
  [AdapterIOStep.CAR]: 'Car',
  [AdapterIOStep.BOOKING]: 'Booking',
  [AdapterIOStep.ORDER]: 'Order',
  [AdapterIOStep.PAYMENT]: 'Payment',
  [AdapterIOStep.INVOICE]: 'Invoice',
  [AdapterIOStep.CREDIT]: 'Credit note',
  [AdapterIOStep.COMMENT]: 'Comment',
  [AdapterIOStep.TIRE]: 'Tire',
  [AdapterIOStep.RIM]: 'Rim',
  [AdapterIOStep.TREAD_DEPTH]: 'Tread depth',
  [AdapterIOStep.TYRE_HOTEL]: 'Tire hotel',
  [AdapterIOStep.WHEEL_CHANGE]: 'Wheel change',
  [AdapterIOStep.WHEEL_INSPECTION]: 'Wheel inspection',
  [AdapterIOStep.STORAGE_FACILITY]: 'Storage facility',
  [AdapterIOStep.STORAGE_OBJECT_GROUP]: 'Wheel set group',
  [AdapterIOStep.WHEEL_SET_STOCK]: 'Wheel set stock',
  [AdapterIOStep.WMS_STOCK]: 'WMS stock'
};
export const ADAPTER_IO_STEP_LABEL_BY_VALUE = ADAPTER_IO_STEP_LABEL_MAP;

const ADAPTER_IO_CHAIN_KIND_LABEL_MAP: Record<AdapterIOChainKind, string> = {
  [AdapterIOChainKind.CREATE]: 'Create',
  [AdapterIOChainKind.UPDATE]: 'Update',
  [AdapterIOChainKind.CANCEL]: 'Cancel',
  [AdapterIOChainKind.REFUND]: 'Refund'
};
/** String index for API/wire values that may not be typed as the enum. */
export const ADAPTER_IO_CHAIN_KIND_LABEL_BY_VALUE: Record<string, string> = ADAPTER_IO_CHAIN_KIND_LABEL_MAP;

const ADAPTER_IO_DIRECTION_LABEL_MAP: Record<AdapterIODirection, string> = {
  [AdapterIODirection.PUSH]: 'Push',
  [AdapterIODirection.PULL]: 'Pull'
};
export const ADAPTER_IO_DIRECTION_OPTIONS = constObjectToOptions(AdapterIODirection, ADAPTER_IO_DIRECTION_LABEL_MAP);
export const ADAPTER_IO_DIRECTION_LABEL_BY_VALUE = ADAPTER_IO_DIRECTION_LABEL_MAP;

/**
 * Subject entity kinds — values match nsdk AdapterIoEntityType.
 * Const object for multi-select filters (not emitted as django-choices).
 */
export const AdapterIOEntityType = {
  BOOKING: 'booking',
  CUSTOMER: 'customer',
  CAR: 'car',
  SALES_ITEM: 'sales_item',
  REFUND: 'refund',
  INVOICE: 'invoice',
  ORGANIZATION: 'organization',
  TIRE: 'tire',
  RIM: 'rim',
  TREAD_DEPTH_READING: 'tread_depth_reading',
  TYRE_HOTEL: 'tyre_hotel',
  WHEEL_INSPECTION: 'wheel_inspection'
} as const satisfies Record<string, AdapterIoEntityType>;
export type AdapterIOEntityTypeValue = AdapterIoEntityType;

const ADAPTER_IO_ENTITY_TYPE_LABEL_MAP: Record<AdapterIoEntityType, string> = {
  booking: 'Booking',
  customer: 'Customer',
  car: 'Car',
  sales_item: 'Product',
  refund: 'Refund',
  invoice: 'Invoice',
  organization: 'Organization',
  tire: 'Tire',
  rim: 'Rim',
  tread_depth_reading: 'Tread depth reading',
  tyre_hotel: 'Tire hotel',
  wheel_inspection: 'Wheel inspection'
};
export const ADAPTER_IO_ENTITY_TYPE_OPTIONS = constObjectToOptions(
  AdapterIOEntityType,
  ADAPTER_IO_ENTITY_TYPE_LABEL_MAP
);
export const ADAPTER_IO_ENTITY_TYPE_LABEL_BY_VALUE = ADAPTER_IO_ENTITY_TYPE_LABEL_MAP;

/**
 * Failure reasons offered as filter options, with the tooltip each one needs.
 *
 * Rows read ``failure_reason_label`` from the API, so this list never has to
 * name every reason the backend can emit: a counted value the API reports and
 * this list omits is added to the filter from the count row's own label
 * (`enrichFiltersWithCounts`). Entries here exist for the descriptions.
 */
const AdapterIOFailureReason = {
  UPSTREAM_BLOCKED: 'upstream_blocked',
  STEP_NOT_APPLICABLE: 'step_not_applicable',
  UNEXPECTED_ERROR: 'unexpected_error',
  MISSING_NAME: 'missing_name',
  MISSING_OR_NON_MOBILE_PHONE: 'missing_or_non_mobile_phone',
  MISSING_IDENTIFICATION_NUMBER: 'missing_identification_number',
  CUSTOMER_TYPE_SKIPPED: 'customer_type_skipped',
  MULTIPLE_USERS_SAME_PHONE: 'multiple_users_same_phone',
  CUSTOMER_RESOLVE_FAILED: 'customer_resolve_failed',
  CUSTOMER_HYDRATE_FAILED: 'customer_hydrate_failed',
  PLACEHOLDER_EMAIL_STRIPPED: 'placeholder_email_stripped',
  EMAIL_LOOKUP_AMBIGUOUS: 'email_lookup_ambiguous',
  MISSING_LICENSE_PLATE: 'missing_license_plate',
  PLATE_OWNERSHIP_CONFLICT: 'plate_ownership_conflict',
  DROP_IN_NOT_IMPORTED: 'drop_in_not_imported',
  MISSING_SERVICE_DEPARTMENT: 'missing_service_department',
  OTHER: 'other'
} as const;
export type AdapterIOFailureReasonValue = (typeof AdapterIOFailureReason)[keyof typeof AdapterIOFailureReason];

const ADAPTER_IO_FAILURE_REASON_LABEL_MAP: Record<AdapterIOFailureReasonValue, string> = {
  upstream_blocked: 'Blocked by upstream step',
  step_not_applicable: 'Step not applicable',
  unexpected_error: 'Unexpected error',
  missing_name: 'Missing customer name',
  missing_or_non_mobile_phone: 'Missing or non-mobile phone',
  missing_identification_number: 'Missing org identification number',
  customer_type_skipped: 'Customer type not ingested',
  multiple_users_same_phone: 'Multiple users same phone',
  customer_resolve_failed: 'Could not resolve customer',
  customer_hydrate_failed: 'Customer hydrate failed',
  placeholder_email_stripped: 'Placeholder email ignored',
  email_lookup_ambiguous: 'Email lookup ambiguous',
  missing_license_plate: 'Missing license plate',
  plate_ownership_conflict: 'Plate ownership conflict',
  drop_in_not_imported: 'Drop-in not imported',
  missing_service_department: 'Missing service department',
  other: 'Other'
};

/** Value-keyed tooltips (mirrors nsdk descriptions when not yet published). */
const ADAPTER_IO_FAILURE_REASON_DESCRIPTION_BY_VALUE: Record<AdapterIOFailureReasonValue, string> = {
  upstream_blocked:
    'This step was skipped because a required earlier step failed or was skipped, so dependents cannot run until the upstream outcome is fixed.',
  step_not_applicable:
    'The step does not apply to this chain (direction, entity type, or payload shape), so it was skipped without treating it as an error.',
  unexpected_error:
    'An unhandled exception occurred while running the step. See failure_detail / last_error for the exception message; often needs code or data fixes rather than a retry alone.',
  missing_name:
    'The external customer has no usable name. Person and business ingest require a non-empty name before a Navio user or organization can be created or updated.',
  missing_or_non_mobile_phone:
    'No valid mobile phone number was available (missing, landline, or unparsable). Phone is the primary identity key for person customers; rows are often left as stubs until a mobile is present.',
  missing_identification_number:
    'Business customers need an organization identification number (e.g. org.nr) when no existing external organization mapping exists, so Navio cannot create or match the organization.',
  customer_type_skipped:
    'The external customer type is intentionally not imported (e.g. subsidiary). The step is skipped rather than failed.',
  multiple_users_same_phone:
    'More than one active Navio user already has this phone number, so create-or-update cannot choose a single account without risking a wrong merge. Resolve the duplicate users first.',
  customer_resolve_failed:
    'Customer resolution failed after available contact data was tried (including optional hydrate). Typical cause is still missing or non-mobile phone, so no Navio user could be linked or created.',
  customer_hydrate_failed:
    'Fetching the full external customer record failed, and the booking list payload alone did not provide a usable contact mobile for import.',
  placeholder_email_stripped:
    'The external email looked like a placeholder or junk address (e.g. test@test.com) and was ignored so it is not written to Navio or used as an identity key.',
  email_lookup_ambiguous:
    'Looking up customers by email returned more than one external customer, so the mapping is ambiguous and cannot be applied safely.',
  missing_license_plate:
    'The external car/booking has no license plate, and Navio car import requires a plate to create or match a vehicle.',
  plate_ownership_conflict:
    'The license plate is already linked to a different customer. Import refuses to reassign ownership silently to avoid moving a car between customers.',
  drop_in_not_imported:
    'Drop-in (walk-in) external bookings are not imported into Navio; they stay external-only by design.',
  missing_service_department:
    'The pull requires a service department with an external location id. Without it, department-scoped external ids and ingest cannot run.',
  other:
    'Catch-all when no more specific failure_reason applies. Prefer a structured reason when possible; use failure_detail for free text.'
};

export const ADAPTER_IO_FAILURE_REASON_OPTIONS = constObjectToOptions(
  AdapterIOFailureReason,
  ADAPTER_IO_FAILURE_REASON_LABEL_MAP
).map((option) => ({
  ...option,
  description: ADAPTER_IO_FAILURE_REASON_DESCRIPTION_BY_VALUE[option.value]
}));

const ADAPTER_TYPE_LABEL_MAP: Record<AdapterType, string> = {
  [AdapterType.EONTYRE]: 'EonTyre'
};
export const ADAPTER_TYPE_OPTIONS = constObjectToOptions(AdapterType, ADAPTER_TYPE_LABEL_MAP);
export const ADAPTER_TYPE_LABEL_BY_VALUE = ADAPTER_TYPE_LABEL_MAP;

const ADAPTER_REVIEW_SEVERITY_LABEL_MAP: Record<AdapterReviewSeverity, string> = {
  [AdapterReviewSeverity.CRITICAL]: 'Critical',
  [AdapterReviewSeverity.ERROR]: 'Error',
  [AdapterReviewSeverity.WARNING]: 'Warning',
  [AdapterReviewSeverity.INFO]: 'Info'
};
export const ADAPTER_REVIEW_SEVERITY_OPTIONS = constObjectToOptions(
  AdapterReviewSeverity,
  ADAPTER_REVIEW_SEVERITY_LABEL_MAP
);
export const ADAPTER_REVIEW_SEVERITY_LABEL_BY_VALUE = ADAPTER_REVIEW_SEVERITY_LABEL_MAP;
